import { useModals } from "@/features/modals/hooks";
import { type ModalDefinition } from "@/features/modals/types";
import {
  type NbaType,
  type Opportunity,
  updateProject,
  useNBA,
} from "@repo/api";
import { creditCardIcon, drillIcon, moneyCashIcon } from "@repo/skapa";
import { translateNbaType, useToast } from "@repo/ui";
import { type Toast, hasItems, isDefined } from "@repo/utils";

interface TaskItem {
  id: number | string;
  icon: (...args: any[]) => any;
  title: string;
  description?: string;
  onClick?: () => void;
}

const getNBAItem = (
  pushModal: (modal: ModalDefinition) => void,
  updateToast: (toast: Toast) => void,
  id: NbaType,
  opportunity: Opportunity
) => {
  switch (id) {
    case "SET_EXPECTED_PURCHASE_DATE":
      return {
        id: `nba-${id}`,
        icon: creditCardIcon,
        title: translateNbaType(id),
        onClick: () =>
          pushModal({
            type: "NEED_ANALYSIS",
            focusField: "expectedPurchaseDate",
          }),
      };
    case "SET_ESTIMATED_BUDGET":
      return {
        id: `nba-${id}`,
        icon: moneyCashIcon,
        title: translateNbaType(id),
        onClick: () =>
          pushModal({ type: "NEED_ANALYSIS", focusField: "budget" }),
      };

    case "SET_SERVICES_INTERESTED":
      return {
        id: `nba-${id}`,
        icon: drillIcon,
        title: translateNbaType(id),
        onClick: () =>
          pushModal({
            type: "SERVICES_INTERESTED",
            servicesInterested: opportunity?.servicesInterested,
            onSave: (services) => {
              return updateProject(
                {
                  id: opportunity.id,
                  servicesInterested: services,
                },
                updateToast
              );
            },
          }),
      };
  }
};

export const useNBADetails = (opportunity: Opportunity) => {
  const { data: nbas, isLoading } = useNBA(opportunity?.id);
  const { updateToast } = useToast();
  const { pushModal } = useModals();

  const actions: TaskItem[] = hasItems(nbas)
    ? nbas
        .sort((a, b) => b.priority - a.priority)
        .filter((nba) => nba.done !== true)
        .map((nba) =>
          getNBAItem(pushModal, updateToast, nba.nbaType, opportunity)
        )
        .filter(isDefined)
    : [];

  return { actions, isLoading };
};
